import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Broadcast, MagnifyingGlass, X } from "@phosphor-icons/react";
import { api } from "@/api";

const MODES = ["scalp", "swing", "position", "macro"];

/**
 * Compact temperature grid across the whole tracked universe.
 * Hue = best score (red→amber→green). Pulse dot = signal-eligible right now.
 * Search bar (with autocomplete via datalist) lets you drill into any pair —
 * clicking or picking a suggestion opens a per-pair Long vs Short heatmap
 * showing the strength of each direction across all 4 modes.
 */
export function LiveHeatmap({ onSelect }) {
  const [mode, setMode] = useState("swing");
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(null); // symbol string currently expanded

  const q = useQuery({
    queryKey: ["heatmap-coverage", mode],
    queryFn: () => api.pairsCoverage(mode, 150),
    refetchInterval: 90_000,
  });

  // Multi-mode view: only fetched once the user focuses on a pair (either by
  // typing or clicking a tile). Cached 90s server-side so the round-trip is
  // ~50ms after the first cold pass.
  const multi = useQuery({
    queryKey: ["heatmap-multi"],
    queryFn: () => api.pairsCoverageMulti(150),
    enabled: !!focused,
    refetchInterval: 120_000,
  });

  const rows = (q.data?.rows || []).filter((r) => !r.error);
  const eligible = rows.filter((r) => r.long_eligible || r.short_eligible).length;
  const avgAdx = rows.reduce((s, r) => s + (r.adx || 0), 0) / (rows.length || 1);
  const avgScore = rows.reduce((s, r) => s + (r.best_score || 0), 0) / (rows.length || 1);
  const trending = rows.filter((r) => r.market_regime === "trend").length;
  const ranging = rows.filter((r) => r.market_regime === "range").length;

  const suggestions = useMemo(() => {
    const qq = query.trim().toUpperCase();
    if (!qq) return rows.slice(0, 12);
    return rows.filter((r) => r.symbol.includes(qq)).slice(0, 12);
  }, [rows, query]);

  const focusedMulti = useMemo(() => {
    if (!focused || !multi.data) return null;
    return (multi.data.rows || []).find((r) => r.symbol === focused);
  }, [focused, multi.data]);

  const commitPick = (sym) => {
    setQuery(sym);
    setFocused(sym);
  };

  return (
    <div className="border border-white/10 bg-black" data-testid="live-heatmap">
      <div className="px-4 py-2 border-b border-white/10 bg-[#0f0f0f] flex items-center gap-2 flex-wrap">
        <Broadcast size={13} className="text-[#00FF66]" />
        <span className="font-display text-[11px] tracking-widest uppercase text-white/70">
          Live Heatmap
        </span>
        <div className="ml-2 flex items-center gap-1">
          {MODES.map((m) => (
            <button
              key={m}
              data-testid={`heatmap-mode-${m}`}
              onClick={() => setMode(m)}
              className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border ${
                mode === m
                  ? "border-[#00FF66]/60 text-[#00FF66] bg-[#00FF66]/10"
                  : "border-white/10 text-white/50 hover:text-white"
              }`}
            >
              {m}
            </button>
          ))}
        </div>

        {/* Searchable pair input — datalist gives native autocomplete */}
        <div className="relative flex items-center gap-1 ml-2">
          <MagnifyingGlass size={11} className="text-white/40 absolute left-1.5 pointer-events-none" />
          <input
            data-testid="heatmap-symbol-search"
            list="heatmap-symbol-list"
            value={query}
            onChange={(e) => setQuery(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter" && suggestions.length) {
                commitPick(suggestions[0].symbol);
              }
              if (e.key === "Escape") {
                setQuery("");
                setFocused(null);
              }
            }}
            onBlur={() => {
              const match = suggestions.find((s) => s.symbol === query);
              if (match) commitPick(match.symbol);
            }}
            placeholder="Type pair (e.g. BTC)"
            className="bg-black border border-white/10 pl-6 pr-2 py-0.5 text-white font-mono text-[11px] w-40"
          />
          <datalist id="heatmap-symbol-list">
            {rows.map((r) => (
              <option key={r.symbol} value={r.symbol}>
                {r.symbol}
              </option>
            ))}
          </datalist>
          {focused && (
            <button
              data-testid="heatmap-clear-focus"
              onClick={() => {
                setFocused(null);
                setQuery("");
              }}
              className="text-white/40 hover:text-white"
              title="Clear focus"
            >
              <X size={12} />
            </button>
          )}
        </div>

        <span className="ml-auto font-mono text-[10px] text-white/40 flex gap-3 flex-wrap">
          <span>
            <span className="text-[#00FF66]">●</span> {eligible} eligible
          </span>
          <span data-testid="heatmap-regime-trend">
            <span className="text-[#00FF66]">▲</span> {trending} trend
          </span>
          <span data-testid="heatmap-regime-range">
            <span className="text-[#F5C518]">◈</span> {ranging} range
          </span>
          <span>Avg ADX {avgAdx.toFixed(1)}</span>
          <span>Avg score {avgScore.toFixed(0)}</span>
          <span>{rows.length} pairs</span>
        </span>
      </div>

      {/* Focused pair: Long vs Short strength across all 4 modes */}
      {focused && (
        <div
          className="p-3 border-b border-white/10 bg-[#080808]"
          data-testid={`heatmap-focus-${focused}`}
        >
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="font-display text-sm tracking-widest uppercase text-white">
              {focused.replace("USDT", "")}
              <span className="text-white/30">/USDT</span>
            </span>
            <span className="font-mono text-[10px] text-white/40">
              Long vs Short strength — all 4 modes · click to open
            </span>
            {focusedMulti && (
              <span className="font-mono text-[10px] text-white/50 ml-auto">
                Best: {focusedMulti.best_mode?.toUpperCase()}{" "}
                <span style={{ color: focusedMulti.best_direction === "long" ? "#00FF66" : "#FF3B30" }}>
                  {focusedMulti.best_direction?.toUpperCase()}
                </span>{" "}
                @ {Math.round(focusedMulti.best_score_overall)}
              </span>
            )}
          </div>
          {multi.isLoading && (
            <div className="font-mono text-[11px] text-white/40 py-2 animate-pulse">
              Fetching all-mode scores...
            </div>
          )}
          {focusedMulti && (
            <div className="grid grid-cols-4 gap-2" data-testid="heatmap-focus-grid">
              {MODES.map((m) => {
                const md = focusedMulti.modes?.[m];
                return (
                  <button
                    key={m}
                    data-testid={`focus-mode-${m}`}
                    onClick={() =>
                      onSelect &&
                      onSelect(focused, md?.best_direction || "long", m)
                    }
                    className="border border-white/10 hover:border-white/40 bg-[#0d0d0d] p-2 text-left"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">
                      {m}
                    </div>
                    {!md && (
                      <div className="font-mono text-[10px] text-white/25">no data</div>
                    )}
                    {md && (
                      <>
                        <ScoreBar label="LONG" value={md.long_score} elig={md.long_eligible} color="#00FF66" />
                        <ScoreBar label="SHORT" value={md.short_score} elig={md.short_eligible} color="#FF3B30" />
                        <div className="mt-1 flex items-center justify-between font-mono text-[9px] text-white/40">
                          <span>ADX {md.adx?.toFixed?.(1) ?? "-"}</span>
                          <span>{md.market_regime || "-"}</span>
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {focused && !multi.isLoading && !focusedMulti && (
            <div className="font-mono text-[11px] text-white/40 py-2">
              No coverage rows for {focused} yet. Try again in ~60s (universe still warming).
            </div>
          )}
        </div>
      )}

      {/* Full universe grid */}
      <div className="p-2 grid grid-cols-6 sm:grid-cols-10 md:grid-cols-14 lg:grid-cols-18 xl:grid-cols-22 gap-1">
        {rows.map((r) => (
          <Tile
            key={r.symbol}
            row={r}
            onSelect={(sym, dir, m) => {
              commitPick(sym);
              onSelect && onSelect(sym, dir, m);
            }}
          />
        ))}
        {rows.length === 0 && (
          <div className="col-span-full py-6 text-center text-white/40 font-mono text-[11px]">
            {q.isLoading ? "Warming up universe (30-60s first load)..." : "No pairs."}
          </div>
        )}
      </div>

      <div className="px-3 py-1.5 border-t border-white/10 flex items-center gap-3 text-[10px] font-mono text-white/40 flex-wrap">
        <span>legend:</span>
        {[
          { c: "#FF3B30", label: "≤50" },
          { c: "#F5C518", label: "50-64" },
          { c: "#8DD35B", label: "65-74" },
          { c: "#00FF66", label: "75+" },
        ].map(({ c, label }) => (
          <span key={label} className="flex items-center gap-1">
            <span className="inline-block w-3 h-3" style={{ background: c }} />
            {label}
          </span>
        ))}
        <span className="flex items-center gap-1">
          <span className="text-[#00FF66]">▲</span>trend
        </span>
        <span className="flex items-center gap-1">
          <span className="text-[#F5C518]">◈</span>range
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-[3px] w-4 bg-[#FF3B30]" />
          long crowd
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-[3px] w-4 bg-[#00FF66]" />
          short crowd
        </span>
        <span className="flex items-center gap-1 ml-auto">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00FF66] pulse-dot" />
          eligible right now
        </span>
      </div>
    </div>
  );
}

// Small horizontal bar showing a directional score (0-100) with a colored fill.
function ScoreBar({ label, value, elig, color }) {
  const v = value || 0;
  const pct = Math.max(4, Math.min(100, v));
  return (
    <div className="mb-0.5">
      <div className="flex items-center justify-between font-mono text-[9px]">
        <span style={{ color }}>{label}</span>
        <span className="text-white/70">
          {v ? Math.round(v) : "-"}
          {elig && <span className="ml-1" style={{ color }}>●</span>}
        </span>
      </div>
      <div className="h-1.5 bg-white/5 relative overflow-hidden">
        <div
          className="absolute left-0 top-0 bottom-0"
          style={{ width: `${pct}%`, background: color, opacity: elig ? 1 : 0.4 }}
        />
      </div>
    </div>
  );
}

function scoreColor(s) {
  if (!s || s < 50) return "#FF3B30";
  if (s < 65) return "#F5C518";
  if (s < 75) return "#8DD35B";
  return "#00FF66";
}

// Map funding regime to a horizontal bar: fill % + color + label.
function sentimentBar(sentiment) {
  if (!sentiment) return null;
  const r = sentiment.funding_regime;
  const pct = Math.abs(sentiment.funding_rate_pct_8h || 0);
  const mag = Math.min(1, pct / 0.1);
  if (r === "crowded_long") {
    return { color: "#FF3B30", side: "right", fill: mag, label: `long crowd ${pct.toFixed(3)}%` };
  }
  if (r === "crowded_short") {
    return { color: "#00FF66", side: "left", fill: mag, label: `short crowd ${pct.toFixed(3)}%` };
  }
  if (r === "leaning") {
    const fr = sentiment.funding_rate_pct_8h || 0;
    return {
      color: fr > 0 ? "#F5C518" : "#8DD35B",
      side: fr > 0 ? "right" : "left",
      fill: mag,
      label: `${fr > 0 ? "long" : "short"} lean ${pct.toFixed(3)}%`,
    };
  }
  return { color: "#666", side: "left", fill: 0.1, label: "neutral funding" };
}

function Tile({ row, onSelect }) {
  const score = row.best_score || 0;
  const color = scoreColor(score);
  const eligible = row.long_eligible || row.short_eligible;
  const dir = row.best_direction;
  const sent = sentimentBar(row.sentiment);
  const mktReg = row.market_regime;
  const regBadge =
    mktReg === "trend" ? "▲" : mktReg === "range" ? "◈" : mktReg === "transition" ? "~" : null;
  const regColor =
    mktReg === "trend" ? "#00FF66" : mktReg === "range" ? "#F5C518" : "#888";
  const title = `${row.symbol}  best=${Math.round(score)} adx=${row.adx?.toFixed?.(1) ?? "-"} regime=${mktReg || "-"} vol=${row.volatility_regime || "-"} src=${row.source}${sent ? "\n" + sent.label : ""}`;
  return (
    <button
      data-testid={`heatmap-${row.symbol}`}
      onClick={() => onSelect && onSelect(row.symbol, dir, "swing")}
      title={title}
      className="relative aspect-square border border-transparent hover:border-white/40 flex flex-col items-center justify-center transition-colors"
      style={{
        background: `linear-gradient(135deg, ${color}22 0%, ${color}55 100%)`,
      }}
    >
      <span className="font-mono text-[9px] font-bold leading-tight text-white truncate w-full text-center px-0.5">
        {row.symbol.replace("USDT", "")}
      </span>
      <span className="font-mono text-[10px] font-bold leading-tight" style={{ color }}>
        {score ? Math.round(score) : "-"}
      </span>
      {eligible && (
        <span
          className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full pulse-dot"
          style={{ background: color }}
        />
      )}
      {regBadge && (
        <span
          className="absolute top-0 left-0.5 font-mono text-[7px] leading-none"
          style={{ color: regColor }}
          data-testid={`heatmap-regime-${row.symbol}`}
        >
          {regBadge}
        </span>
      )}
      {sent && (
        <div
          className="absolute bottom-0 left-0 right-0 h-[3px] bg-black/50 overflow-hidden"
          data-testid={`heatmap-sentiment-${row.symbol}`}
        >
          <div
            className="h-full transition-all"
            style={{
              width: `${Math.max(6, sent.fill * 100)}%`,
              background: sent.color,
              marginLeft: sent.side === "right" ? "auto" : 0,
            }}
          />
        </div>
      )}
      {row.source === "mexc" && !sent && (
        <span className="absolute bottom-0 left-0.5 text-[7px] font-mono uppercase text-[#F5C518]/80">
          m
        </span>
      )}
    </button>
  );
}
